import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import { PROGRAM_STEP_FLAG, PROGRAM_STEP_FLAG_TIMEOUT_MS, programStepFromFlag, type ProgramStep } from '@/components/ApplicationForm/programStep';

/**
 * A new application's program-step variant (the experiment flag
 * `application-program-step-experiment`). `undefined` while PostHog has not
 * answered; then the flag's `control` or `test` (source `flag`), or control
 * with source `fallback` once the flags load without a variant for this person
 * or {@link PROGRAM_STEP_FLAG_TIMEOUT_MS} pass.
 *
 * Mount it only while a new application waits for its variant: evaluating the
 * flag is the experiment's exposure (`$feature_flag_called`), so it must not
 * run for visitors who never start one. Existing applications use their stored
 * variant (`programStepForApplication`) instead.
 */
export function useProgramStepAssignment() {
  const $ = _c(9);
  const flagValue = useFeatureFlagVariantKey(PROGRAM_STEP_FLAG);
  const posthog = usePostHog();
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  let t0;
  let t1;
  if ($[0] !== posthog) {
    t0 = () => {
      const unsubscribe = posthog?.onFeatureFlags?.(() => setFlagsLoaded(true));
      return () => {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
      };
    };
    t1 = [posthog];
    $[0] = posthog;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const timer = setTimeout(() => setTimedOut(true), PROGRAM_STEP_FLAG_TIMEOUT_MS);
      return () => clearTimeout(timer);
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== flagValue || $[6] !== flagsLoaded || $[7] !== timedOut) {
    t4 = programStepFromFlag(flagValue, {
      flagsLoaded,
      timedOut
    });
    $[5] = flagValue;
    $[6] = flagsLoaded;
    $[7] = timedOut;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}